import {
    EMPTY_PARENT_PAYLOAD,
    type ItemsParentMutateAsync,
    type ParentIds,
    createParentCache,
    pickParentPayloadFields,
    resolveParentsForRequests,
} from '../VideoCarousel/linkedParentResolver';

export type { ParentIds, ItemsParentMutateAsync };

export const EMPTY_PARENT_LINK_PAYLOAD: ParentIds = { ...EMPTY_PARENT_PAYLOAD };

export { createParentCache, resolveParentsForRequests };

const cacheKey = (type: string, parentId: string) =>
    `${type.trim().toUpperCase()}:${String(parentId).trim()}`;

function getFromCache(
    cache: Map<string, ParentIds>,
    parentId: string,
    entityType: string
): ParentIds {
    return cache.get(cacheKey(entityType, parentId)) ?? { ...EMPTY_PARENT_LINK_PAYLOAD };
}

/** Collect unique SHOW / COLLECTION IDs for parent resolution. */
export function collectShowCardParentRequests(
    sourceType: string,
    widgetItems: Array<{
        id?: string;
        redirection_id?: string;
    }>,
    options: { sourceId?: string } = {}
): Array<{ parentId: string; type: string }> {
    const normalizedSource = sourceType.trim();
    const requests: Array<{ parentId: string; type: string }> = [];
    const seen = new Set<string>();

    const add = (parentId: string, type: string) => {
        const id = String(parentId).trim();
        if (!id) return;
        const key = cacheKey(type, id);
        if (seen.has(key)) return;
        seen.add(key);
        requests.push({ parentId: id, type: type.toUpperCase() });
    };

    if (normalizedSource === 'Collection' && options.sourceId) {
        add(options.sourceId, 'COLLECTION');
    }

    widgetItems.forEach((item) => {
        const showId = item.redirection_id || item.id;
        if (showId) add(showId, 'SHOW');
    });

    return requests;
}

/** Widget-level parent fields from source selection. */
export function buildShowCardSourceParentFields(
    sourceType: string,
    cache: Map<string, ParentIds>,
    options: {
        sourceId?: string;
        sourceName?: string;
        categoryId?: string;
        categoryName?: string;
    } = {}
): ParentIds {
    const normalized = sourceType.trim();

    if (normalized === 'Collection' && options.sourceId) {
        const resolved = getFromCache(cache, String(options.sourceId), 'COLLECTION');
        return pickParentPayloadFields({
            show_id: '',
            show_name: '',
            collection_id: resolved.collection_id || String(options.sourceId),
            collection_name: resolved.collection_name || options.sourceName || '',
            category_id: resolved.category_id,
            category_name: resolved.category_name,
        });
    }

    if (normalized === 'Category') {
        const categoryId = String(options.categoryId ?? options.sourceId ?? '').trim();
        return pickParentPayloadFields({
            ...EMPTY_PARENT_LINK_PAYLOAD,
            category_id: categoryId,
            category_name: options.categoryName || '',
        });
    }

    return { ...EMPTY_PARENT_LINK_PAYLOAD };
}

/** Apply resolved SHOW parents at item level (card_details unchanged). */
export function enrichShowCardWidgetItems(
    widgetItems: any[],
    cache: Map<string, ParentIds>
): any[] {
    return widgetItems.map((item) => {
        const showId = String(item.redirection_id || item.id || '').trim();

        if (!showId) {
            return {
                ...item,
                ...EMPTY_PARENT_LINK_PAYLOAD,
            };
        }

        const resolved = pickParentPayloadFields(getFromCache(cache, showId, 'SHOW'));

        return {
            ...item,
            redirection_to: 'SHOW',
            redirection_id: showId,
            show_id: resolved.show_id || showId,
            show_name: resolved.show_name,
            collection_id: resolved.collection_id,
            collection_name: resolved.collection_name,
            category_id: resolved.category_id,
            category_name: resolved.category_name,
        };
    });
}

export function shouldResolveShowCardParents(
    contentType: string,
    sourceType: string
): boolean {
    if (contentType !== 'Static') return false;
    return ['Collection', 'Manual', 'Category'].includes(sourceType);
}
